// API Configuration
// This file manages the backend URL for different environments

// Import storage cleanup utility to prevent URL duplication issues
if (typeof window !== 'undefined') {
  import('./clearStorageUtil.js').catch(err => 
    console.log('Storage cleanup utility not available:', err.message)
  );
}

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

// ✅ Detect Capacitor mobile app
const isCapacitor = typeof window !== 'undefined' && 
  (window.location.protocol === 'capacitor:' || 
   window.location.protocol === 'ionic:' ||
   window.Capacitor !== undefined);

// ✅ Auto-detect local development and mobile access
const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const isNetlify = typeof window !== 'undefined' && 
  (window.location.hostname.includes('netlify.app') || window.location.hostname.includes('.netlify.'));

const isClassHero = typeof window !== 'undefined' && 
  (window.location.hostname.includes('classhero.com') || window.location.hostname.includes('classhero.'));

// Detect which school based on subdomain
const getSchoolFromDomain = () => {
  if (typeof window === 'undefined') return 'sherborne_qatar';
  
  const hostname = window.location.hostname;
  if (hostname.includes('sherborneboys')) return 'sherborne_boys';
  if (hostname.includes('sherbornebh')) return 'sherborne_bh';
  if (hostname.includes('demo')) return 'demo';
  return 'sherborne_qatar'; // Default
};

// Get backend URL for each school
const getSchoolBackend = (school) => {
  const backends = {
    'sherborne_qatar': 'https://ai-school-backend-production.up.railway.app',
    'sherborne_bh': 'https://ai-school-backend-production.up.railway.app', // Shared for now
    'sherborne_boys': 'https://web-production-d2840.up.railway.app', // New backend for Sherborne Boys
    'demo': 'https://web-production-1ef5a.up.railway.app' // Separate backend
  };
  return backends[school] || backends['sherborne_qatar'];
};

// currentSchool will be exported later - removed duplicate declaration
const currentBackendUrl = getSchoolBackend(getSchoolFromDomain());

const isMobileLocalAccess = typeof window !== 'undefined' && 
  !isNetlify && !isClassHero && // Only apply mobile detection when NOT on Netlify or ClassHero
  (window.location.hostname === '192.168.1.14' || 
   window.location.hostname === '10.3.125.13' ||  // Add your server IP
   window.location.hostname.startsWith('192.168.') ||
   window.location.hostname.startsWith('10.3.125.') ||  // Add your network range
   window.location.hostname.endsWith('.local'));

// Configuration object
const config = {
  // Local development: Use local backend for testing (chat uploads, etc.)
  localhost: {
    API_BASE_URL: 'http://localhost:8000',
    WEBSOCKET_URL: 'ws://localhost:8000'
  },
  
  // Mobile access: Use network IP backend (dynamically detect backend IP)
  mobile: {
    API_BASE_URL: (() => {
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        // If accessing via specific IP, use same IP for backend but port 8000
        if (hostname === '10.3.125.13') return 'http://10.3.125.13:8000';
        if (hostname === '192.168.1.14') return 'http://192.168.1.14:8000';
        if (hostname.startsWith('192.168.')) return `http://${hostname}:8000`;
        if (hostname.startsWith('10.3.125.')) return `http://${hostname}:8000`;
      }
      return 'http://192.168.1.14:8000'; // Fallback
    })(),
    WEBSOCKET_URL: (() => {
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        // If accessing via specific IP, use same IP for WebSocket but port 8000
        if (hostname === '10.3.125.13') return 'ws://10.3.125.13:8000';
        if (hostname === '192.168.1.14') return 'ws://192.168.1.14:8000';
        if (hostname.startsWith('192.168.')) return `ws://${hostname}:8000`;
        if (hostname.startsWith('10.3.125.')) return `ws://${hostname}:8000`;
      }
      return 'ws://192.168.1.14:8000'; // Fallback
    })()
  },

  // Cloudflare tunnel: Use tunnel URL for everything (no port forwarding needed)
    cloudflare: {
      name: "Cloudflare Tunnel",
      WEBSOCKET_URL: "https://leaves-jam-sally-intimate.trycloudflare.com",
      API_BASE_URL: "https://leaves-jam-sally-intimate.trycloudflare.com",
      isProduction: true
    },  
    
  // Netlify deployment: Use Railway backend directly (no tunnel needed)
  // NOTE: Do NOT use REACT_APP_API_BASE_URL here — it overrides runtime subdomain detection
  // and forces all schools to the same backend. currentBackendUrl detects the school at runtime.
  netlify: {
    name: "Netlify to Railway",
    API_BASE_URL: currentBackendUrl,
    WEBSOCKET_URL: currentBackendUrl.replace('https://', 'wss://'),
    isProduction: true
  },
  
  // ClassHero deployment: Use Railway backend directly
  classhero: {
    name: "ClassHero to Railway",
    API_BASE_URL: currentBackendUrl,
    WEBSOCKET_URL: currentBackendUrl.replace('https://', 'wss://'),
    isProduction: true
  },
  
  // Development: Railway backend (for deployed dev builds)
  development: {
    API_BASE_URL: currentBackendUrl,
    WEBSOCKET_URL: currentBackendUrl.replace('https://', 'wss://')
  },
  
  // Production: Railway backend
  production: {
    API_BASE_URL: currentBackendUrl,
    WEBSOCKET_URL: currentBackendUrl.replace('https://', 'wss://')
  }
};

// ✅ Smart environment detection
const getEnvironment = () => {
  // Priority 0: Capacitor mobile app - always use production Railway backend
  if (isCapacitor) {
    console.log('🔵 Capacitor detected - using Railway backend');
    return 'production';
  }
  // Priority 1: Check for Cloudflare tunnel override
  if (process.env.REACT_APP_USE_CLOUDFLARE === 'true' || 
      (typeof window !== 'undefined' && window.localStorage?.getItem('useCloudflare') === 'true')) {
    return 'cloudflare';
  }
  // Priority 2: Use Railway backend directly when on ClassHero
  if (isClassHero) {
    return 'classhero';
  }
  // Priority 3: Use Railway backend directly when on Netlify (better reliability)
  if (isNetlify) {
    return 'netlify';
  }
  // Priority 4: Mobile access to local development (only when not on Netlify or ClassHero)
  if (isMobileLocalAccess) return 'mobile';
  // Priority 5: Local development on computer
  if (isLocalhost) return 'localhost';
  // Priority 6: Development build (React NODE_ENV)
  if (isDevelopment) return 'development';
  // Priority 7: Production (default for other hosting platforms)
  return 'production';
};

const currentEnvironment = getEnvironment();
const currentConfig = config[currentEnvironment];

// Export the API base URL
export const API_BASE_URL = currentConfig.API_BASE_URL;
export const WEBSOCKET_URL = currentConfig.WEBSOCKET_URL;
export const TUNNEL_VIDEO_URL = currentConfig.TUNNEL_VIDEO_URL;
export const CURRENT_ENVIRONMENT = currentEnvironment;
export const currentSchool = getSchoolFromDomain(); // Export currentSchool for staff file routing

// 🔍 Debug: Log which school/backend is being used (helps diagnose cross-school issues)
if (typeof window !== 'undefined') {
  console.log(`🏫 School: ${currentSchool} | Env: ${currentEnvironment} | Backend: ${API_BASE_URL}`);
}

// Helper function to get video URL for students (with Cloudflare tunnel support)
export const getStudentVideoUrl = (studentId, studentIp = null) => {
  if (currentEnvironment === 'cloudflare') {
    // Use Cloudflare tunnel for video streaming
    return `${TUNNEL_VIDEO_URL}?student=${studentId}`;
  } else {
    // Use direct IP connection (traditional method)
    return studentIp ? `http://${studentIp}:8080/video` : null;
  }
};

// Helper function to build API URLs
export const buildApiUrl = (endpoint) => {
  // Handle empty endpoint - return base URL without trailing slash
  if (!endpoint || endpoint === '') {
    return API_BASE_URL;
  }
  
  // Safety check: If endpoint already contains the full railway URL, return as-is
  if (typeof endpoint === 'string' && endpoint.includes('railway.app')) {
    console.warn('⚠️ buildApiUrl received full URL as endpoint:', endpoint);
    return endpoint;
  }
  
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  const fullUrl = `${API_BASE_URL}/${cleanEndpoint}`;
  
  // Safety check: Detect and fix duplicate domains
  if (fullUrl.includes('railway.apprailway.app') || fullUrl.includes('railway.appai-school-backend')) {
    console.error('🚨 URL duplication detected! Input:', endpoint, 'Output would be:', fullUrl);
    // Extract the correct endpoint part and rebuild
    const pathMatch = endpoint.match(/railway\.app(.*)$/);
    if (pathMatch) {
      const correctPath = pathMatch[1];
      return `${API_BASE_URL}${correctPath}`;
    }
  }
  
  return fullUrl;
};

// Helper function to build WebSocket URLs
export const buildWebSocketUrl = () => {
  return WEBSOCKET_URL;
};

// Helper function for backwards compatibility
export const getServerUrl = () => API_BASE_URL;

// Log current configuration (for debugging)
console.log('🌐 Environment Detection:', {
  protocol: typeof window !== 'undefined' ? window.location.protocol : 'server-side',
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'server-side',
  NODE_ENV: process.env.NODE_ENV,
  isCapacitor: isCapacitor,
  isLocalhost: isLocalhost,
  isNetlify: isNetlify,
  isClassHero: isClassHero,
  isMobileLocalAccess: isMobileLocalAccess,
  currentEnvironment: currentEnvironment,
  currentSchool: currentSchool,
  API_BASE_URL: API_BASE_URL,
  WEBSOCKET_URL: WEBSOCKET_URL
});

export default {
  API_BASE_URL,
  WEBSOCKET_URL,
  TUNNEL_VIDEO_URL,
  CURRENT_ENVIRONMENT,
  CURRENT_SCHOOL: currentSchool,
  getStudentVideoUrl,
  buildApiUrl,
  getServerUrl
};
